import React, { useEffect, useState } from 'react'

interface RunSummary {
    id: string
    title: string
    status: 'queued' | 'generating' | 'complete' | 'incomplete' | 'failed'
    story_count: number
    incomplete_due_to_cap: number
    estimated_total_seconds: number | null
    actual_total_seconds: number | null
    created_at: string
    source_title: string
    source_date: string | null
    progress_message?: string
    total_tokens?: number
    bundle_url?: string
    audio_url?: string
}

interface DashboardProps {
    onNew: () => void
}

export default function Dashboard({ onNew }: DashboardProps) {
    const [runs, setRuns] = useState<RunSummary[]>([])
    const [loading, setLoading] = useState(true)
    const [playingId, setPlayingId] = useState<string | null>(null)
    const [reportedDurations, setReportedDurations] = useState<Set<string>>(new Set())

    const handleReportDuration = (id: string, durationSecs: number) => {
        if (reportedDurations.has(id)) return
        fetch(`/api/runs/${id}/duration`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ actualDurationSeconds: durationSecs })
        }).catch(console.error)
        setReportedDurations(prev => new Set(prev).add(id))
    }

    const fetchRuns = async () => {
        try {
            const res = await fetch('/api/runs')
            if (res.ok) {
                const data = await res.json() as any
                setRuns(data.runs || [])
            }
        } catch (e) {
            console.error('Failed to fetch runs', e)
        } finally {
            setLoading(false)
        }
    }

    const cancelRun = async (id: string) => {
        if (!confirm('Are you sure you want to cancel this episode? Any text or audio generated so far will be lost.')) return
        try {
            const res = await fetch(`/api/runs/${id}`, { method: 'PATCH' })
            if (res.ok) fetchRuns()
        } catch (e) {
            console.error('Failed to cancel run', e)
        }
    }

    useEffect(() => {
        fetchRuns()
        // Poll every 5s if any runs are active
        const interval = setInterval(() => {
            setRuns(current => {
                if (current.some(r => r.status === 'queued' || r.status === 'generating')) {
                    fetchRuns()
                }
                return current
            })
        }, 5000)
        return () => clearInterval(interval)
    }, [])

    const getStatusBadge = (status: RunSummary['status']) => {
        switch (status) {
            case 'complete': return <span className="badge badge-success">Complete</span>
            case 'incomplete': return <span className="badge badge-warning">Incomplete (Cap)</span>
            case 'failed': return <span className="badge badge-error">Failed</span>
            case 'queued': return <span className="badge badge-neutral">Queued</span>
            case 'generating': return (
                <span className="badge badge-info flex gap-sm items-center">
                    <span className="w-2 h-2 rounded-full bg-info animate-pulse"></span>
                    Generating
                </span>
            )
            default: return <span className="badge badge-neutral">{status}</span>
        }
    }

    const formatDuration = (seconds: number | null) => {
        if (seconds == null) return '--'
        const m = Math.floor(seconds / 60)
        const s = Math.round(seconds % 60)
        return `${m}m ${s}s`
    }

    if (loading) {
        return <div className="text-center mt-lg text-secondary">Loading episodes...</div>
    }

    return (
        <div className="animate-fade-in">
            <div className="flex justify-between items-center mb-lg">
                <h2>Recent Episodes</h2>
                <button className="btn btn-primary" onClick={onNew}>+ New Episode</button>
            </div>

            {runs.length === 0 ? (
                <div className="card text-center" style={{ padding: '40px' }}>
                    <div style={{ fontSize: '3rem', marginBottom: '16px' }}>🎙️</div>
                    <h3>No episodes yet</h3>
                    <p>Upload a newsletter email to get started.</p>
                    <button className="btn btn-primary mt-sm" onClick={onNew}>Create First Episode</button>
                </div>
            ) : (
                <div className="grid gap-md">
                    {runs.map(run => (
                        <div key={run.id} className="card interactive flex-col gap-sm">
                            <div className="flex justify-between items-center">
                                <h3 style={{ margin: 0 }}>{run.title}</h3>
                                {getStatusBadge(run.status)}
                            </div>
                            <div className="text-sm text-secondary flex items-center flex-wrap" style={{ gap: '16px' }}>
                                <span><strong>Stories:</strong> {run.story_count}</span>
                                <span><strong>Duration:</strong> {formatDuration(run.actual_total_seconds || run.estimated_total_seconds)}</span>
                                {run.total_tokens != null && <span><strong>Tokens:</strong> {run.total_tokens.toLocaleString()}</span>}
                                <span><strong>Created:</strong> {new Date(run.created_at).toLocaleString()}</span>
                            </div>

                            {run.status === 'complete' || run.status === 'incomplete' ? (
                                <div className="mt-sm flex gap-sm items-center">
                                    {run.audio_url && (
                                        <>
                                            {playingId === run.id ? (
                                                <audio
                                                    controls
                                                    src={run.audio_url}
                                                    autoPlay
                                                    onLoadedMetadata={e => {
                                                        const d = e.currentTarget.duration
                                                        if (d && d > 0 && !run.actual_total_seconds) {
                                                            handleReportDuration(run.id, d)
                                                        }
                                                    }}
                                                    style={{ height: '36px', borderRadius: '4px' }}
                                                />
                                            ) : (
                                                <button className="btn btn-primary text-sm flex items-center gap-xs" onClick={() => setPlayingId(run.id)}>
                                                    <span>▶ Play</span>
                                                </button>
                                            )}

                                            <a href={run.audio_url} target="_blank" rel="noreferrer" className="btn btn-secondary text-sm">
                                                Download (.mp3)
                                            </a>
                                        </>
                                    )}

                                </div>
                            ) : null}

                            {(run.status === 'queued' || run.status === 'generating') && (
                                <div className="mt-sm flex justify-between items-end">
                                    <div className="flex-1 mr-md">
                                        <div className="text-xs text-secondary mb-xs">
                                            {run.progress_message || 'Initializing pipeline...'}
                                        </div>
                                        <div className="progress-bg">
                                            <div className="progress-bar progress-indeterminate"></div>
                                        </div>
                                    </div>
                                    <button
                                        className="btn btn-secondary text-xs"
                                        style={{ color: 'var(--status-error)', borderColor: 'var(--status-error)' }}
                                        onClick={() => cancelRun(run.id)}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}
